/**
 * Cache keys for the organization- and project-scoped settings collections.
 *
 * They live in one module because the same key is built in two places: the query definition that
 * fetches the collection, and the Mercure handler that writes the live update into the cache with
 * `setQueryData`. Whenever those two drifted apart the write landed on a key nobody read and the
 * view silently stopped updating — `Labels.tsx` did exactly that, writing project label groups to
 * the organization key.
 *
 * Every key carries the identity of what it holds, because a key that omits it aliases:
 *  - without `projectId` all projects in an organization shared ONE entry, so `ensureQueryData`
 *    handed project A's issue types to project B's settings screen;
 *  - without `organizationId` the entry survived a change of active organization. Relying on the
 *    switch to purge is not enough — the superuser console repoints `activeMembership` while
 *    purging only unrelated prefixes (useActivateOrganization, MembershipRow), and a membership
 *    deleted server-side is repointed by the backend and merely adopted by `useSyncUser`. Identity
 *    in the key makes aliasing impossible on every path instead of on the paths we remembered.
 *
 * The FIRST element is unchanged on purpose: `MUTATION_INVALIDATION_MAP` in api-client.ts, the
 * organization-switch purge, and several literal `invalidateQueries` call sites all match by
 * prefix, and must keep matching.
 */
export type SettingsScope = {
  /** Present for a project's own settings; absent for the organization-wide ones. */
  projectId?: string
  /** Active organization id. Normalised to null when unknown, so the key stays serialisable. */
  organizationId?: number | string
}

const organizationScope = (organizationId: SettingsScope['organizationId']) => ({
  organizationId: organizationId ?? null,
})

export const settingsQueryKeys = {
  issueTypes: ({ projectId, organizationId }: SettingsScope): unknown[] =>
    projectId
      ? ['projectIssueTypes', { projectId }]
      : ['organizationIssueTypes', organizationScope(organizationId)],

  statusGroups: ({ projectId, organizationId }: SettingsScope): unknown[] =>
    projectId
      ? ['projectStatusGroups', { projectId }]
      : ['organizationStatusGroups', organizationScope(organizationId)],

  labelGroups: ({ projectId, organizationId }: SettingsScope): unknown[] =>
    projectId
      ? ['projectLabelGroups', { projectId }]
      : ['organizationLabelGroups', organizationScope(organizationId)],

  stageParticipations: (stageId: string): unknown[] => [
    'getProjectStageParticipations',
    { stageId },
  ],
}
